import { homeFor, mayOpen, pathOnly } from '@/lib/roleRouting';

/**
 * Post-login destination handling.
 *
 * When an expired session interrupts someone, we send them to /auth/login with the
 * page they wanted in `?next=`, then return them there afterwards. That parameter is
 * attacker-controllable — anyone can hand out a link to our own login page carrying
 * any `next` they like — so it must never be able to navigate off-site. A phishing
 * page reached by "click our real login link, then get bounced somewhere else" is
 * exactly the open-redirect pattern credential-harvesting relies on.
 *
 * The rule is deliberately strict: a single leading slash, followed by something
 * that is not another slash or a backslash.
 *
 * The backslash case is the subtle one. `/\evil.com` passes a naive
 * `startsWith('/') && !startsWith('//')` check, but browsers normalise the backslash
 * to a forward slash, turning it into the protocol-relative `//evil.com` — a
 * fully-qualified URL pointing at someone else's host.
 *
 * Leading whitespace and control characters are stripped by browsers before parsing,
 * so a value like "\n//evil.com" would slip past a check that looked at the raw
 * first character. We reject anything containing whitespace outright.
 */
export function safeNextPath(search: string): string | null {
  let next: string | null;
  try {
    next = new URLSearchParams(search).get('next');
  } catch {
    return null;
  }
  if (!next) return null;

  // Browsers ignore leading/trailing C0 control characters and spaces when resolving
  // a URL, so normalise the same way before validating rather than after.
  const trimmed = next.replace(/^[ - ]+/, '').replace(/[ - ]+$/, '');

  // One leading slash, next character is neither slash nor backslash, and no
  // whitespace anywhere. This admits "/admin/analytics?tab=1" and rejects
  // "//evil.com", "/\evil.com", "https://evil.com", "javascript:..." and "\\evil.com".
  if (!/^\/(?![/\\])[^\s]*$/.test(trimmed)) return null;

  return trimmed;
}

/**
 * Where to send a user once they are authenticated.
 *
 * With the role: a `next` the role may not open is replaced by that role's home, rather than
 * followed and then bounced. For an Interviewer that means a link to a candidate's report
 * lands on My interviews, not on a refusal — and the app never asks the server for the
 * report on their behalf. Without a role (or for staff) it is exactly what it always was.
 */
export function postLoginDestination(search: string, role?: string | null): string {
  const next = safeNextPath(search);
  if (next && mayOpen(role, pathOnly(next))) return next;
  return homeFor(role);
}
